import PropTypes from 'prop-types';
import React, { useEffect } from 'react';
import * as Util from '../../../util';
import { useCommunities } from '../../Communities/containers/communitiesDataStore';
import CommunityImage from '../../Community/CommunityImage';
import AddEditTopic from '../AddEditTopic';
import { useComments } from '../containers/commentsDataStore';
import ConversationStatus from '../ConversationStatus';
import CommentInputFrame from './CommentInputFrame';
import './comments.less';
import CommunityOptions from './CommunityOptions';
import ParentCommentFrame from './ParentCommentFrame';
import HeaderActionOptions from './HeaderActionOptions/HeaderActionOptions';
import { Translate } from '@above-the-treeline/localization';
import CustomShowMoreText from '../../UI/CustomShowMoreText/CustomShowMoreText';
import LoadingSpinnerInContainer from '../../UI/LoadingSpinnerInContainer';

Comments.propTypes = {
	communityId: PropTypes.number,
	text: PropTypes.oneOfType([PropTypes.string, PropTypes.element]),
	isOwner: PropTypes.bool,
	conversationId: PropTypes.number,
	isMobile: PropTypes.bool,
};

export default function Comments({
	communityId,
	text,
	isOwner,
	conversationId,
	isMobile,
}) {
	const [selectedCommunityId, setSelectedCommunityId] = React.useState(0);
	const [overrideDefault, setOverrideDefault] = React.useState(false);
	const [status, setStatus] = React.useState(ConversationStatus.active);
	const [maxThoughts, setMaxThoughts] = React.useState(6);

	let messagesEnd = React.createRef();

	const commentsDataStore = useComments();
	const { threads, type, isLoading } = commentsDataStore.state;

	const { getCommunities } = useCommunities();
	const communities = getCommunities();

	useEffect(() => {
		if (communityId > 0) {
			setSelectedCommunityId(communityId);
		}
	}, []);

	useEffect(() => {
		commentsDataStore.getScrollOnUpdate() && scrollToBottom();
	}, [threads]);

	const scrollToBottom = () => {
		if (messagesEnd && messagesEnd.current) {
			messagesEnd.current.scrollIntoView({ behavior: 'instant' });
		}
	};

	const showMore = () => {
		setMaxThoughts(maxThoughts * 1 + 6);
	};

	const goToCommunity = passedCommunityId => {
		setSelectedCommunityId(passedCommunityId);
		setOverrideDefault(true);
	};

	const goToCommunityOptions = () => {
		goToCommunity(0);
	};

	const getCommunityName = communityId => {
		const community =
			communities &&
			communities.find(com => Number(com.id) === Number(communityId));
		return community && community.name;
	};

	const renderThread = thread => {
		const { comments } = thread;
		return (
			<React.Fragment>
				{comments && (
					<ParentCommentFrame
						comments={comments}
						refreshAfterViewing={refreshAfterViewing}
						markAsRead={markAsRead}
					/>
				)}
			</React.Fragment>
		);
	};

	const markAsRead = () => {
		setStatus(ConversationStatus.read);
	};

	const changeStatus = status => {
		setStatus(status);
		commentsDataStore &&
			commentsDataStore.changeConversationStatus(
				selectedCommunityId,
				status,
			);
	};

	const refreshAfterViewing = () => {
		commentsDataStore.refresh(true);
	};

	const addEditTopicIcon = (
		<div className="column-spaced">
			<AddEditTopic
				communityId={selectedCommunityId}
				topicId={conversationId}
				name={text}
			/>
		</div>
	);

	const renderTopicTitleAndActions = communitiesWithThreads => {
		const typeLabel = type && `${type.label}: `;
		const textToTruncate = (
			<span>
				{typeLabel}
				{text}
			</span>
		);
		return (
			<div className="topic-title-and-actions">
				<CustomShowMoreText text={textToTruncate} />
				{isOwner && addEditTopicIcon}
				<HeaderActionOptions
					changeStatus={changeStatus}
					communitiesWithThreads={communitiesWithThreads}
					isMobile={isMobile}
					goToCommunityOptions={goToCommunityOptions}
					status={status}
					type={type}
				/>
			</div>
		);
	};

	const renderCommentsHeader = (
		threadCommunityId,
		communitiesWithThreads,
	) => {
		const communityName = getCommunityName(threadCommunityId);
		return (
			<div className="comments-header">
				<div className="comments-header-main">
					<CommunityImage
						communityId={threadCommunityId}
						dimensions={50}
					/>
					<div className="header-right">
						<div className="community-name bold">
							<CustomShowMoreText
								text={
									<Translate
										id="x_community_conversation_title"
										data={{ communityName: communityName }}
									/>
								}
							/>
						</div>
						{!isMobile &&
							renderTopicTitleAndActions(communitiesWithThreads)}
					</div>
				</div>
				{isMobile && renderTopicTitleAndActions(communitiesWithThreads)}
			</div>
		);
	};

	if (!isLoading) {
		const communityIds = Util.groupBy(threads, 'communityId');
		const communitiesWithThreads = Object.keys(communityIds).length;
		if (
			selectedCommunityId > 0 ||
			(communitiesWithThreads === 1 && overrideDefault == false)
		) {
			const threadCommunityId = selectedCommunityId
				? selectedCommunityId
				: communitiesWithThreads === 1 && Object.keys(communityIds)[0];
			const communityThreads =
				threads &&
				threads.filter(
					th => th.communityId === parseInt(threadCommunityId),
				);
			return (
				<div className="conversation-comments">
					{renderCommentsHeader(
						threadCommunityId,
						communitiesWithThreads,
					)}
					<div className="comments-body">
						{communityThreads.length > 0 ? (
							communityThreads.map((thread, idx) => (
								<React.Fragment key={`thread-${idx}`}>
									{communityThreads.length - maxThoughts ===
									idx + 1 ? (
										<div onClick={showMore}>Load More</div>
									) : (
										<React.Fragment>
											{communityThreads.length -
												maxThoughts <=
												idx + 1 && (
												<React.Fragment key={thread.id}>
													{renderThread(thread)}
												</React.Fragment>
											)}
										</React.Fragment>
									)}
								</React.Fragment>
							))
						) : (
							<span className="no-activity-message">
								<Translate id="no_activity" />
							</span>
						)}
						<div
							style={{ float: 'left', clear: 'both' }}
							ref={messagesEnd}></div>
					</div>
					<div className="comments-footer">
						<CommentInputFrame
							communityId={threadCommunityId}
							threadCount={threads.length}
						/>
					</div>
				</div>
			);
		} else {
			return (
				<CommunityOptions
					threadsByCommunityId={communityIds}
					goToCommunity={goToCommunity}
				/>
			);
		}
	} else {
		return (
			<div className="conversation-comments conversations-spinner">
				<LoadingSpinnerInContainer height="100%" />
			</div>
		);
	}
}
